import React, { useCallback, useMemo, useState } from 'react';
import { ReactFlow, Background, BackgroundVariant, Node, Edge, Position, Handle, NodeProps } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Step, Workflow } from '../api/models/Workflows';
import { createWorkflow } from '../api/thunks/workflows';
import { useAppSelector } from '../store/hooks';
import { Mail, Cpu, DatabaseIcon, Clock, Diamond, PlugIcon, CurlyBraces, Zap } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { setNotification } from '../store/notifications-reducer';
import { useNavigate } from 'react-router-dom';

interface WorkflowPreviewComponentProps {
    workflowJson: string;
    onWorkflowCreated?: () => void;
}

// Matches the API format from gen/server/models
interface WorkflowData {
    name: string;
    enabled: boolean;
    steps: Array<{
        stepType: string;
        order: number;
        dataSourceStep?: {
            ticker: string;
            subType: string;
        };
        llmStep?: {
            model: string;
            prompt: string;
            useSystemPrompt: boolean;
            structuredOutput?: string;
        };
        emailStep?: {
            subject: string;
            autoTransformBody: boolean;
        };
        decisionStep?: {
            isSmartDecisionStep: boolean;
            model: string;
            smartDecisionStepQuestion: string;
            targetAttribute: string;
            targetValue: string;
            operator: string;
            attributeType: string;
        };
        scheduleStep?: {
            schedule: string;
        };
        jsonTransformationStep?: {
            jmespath: string;
        };
        apiDestinationStep?: {
            url: string;
            method: string;
            authHeaderName: string;
            authHeaderSecretName: string;
        };
    }>;
}

// Mini node components for preview
const PreviewDataSourceNode = ({ data }: NodeProps<Node<{ ticker: string; dataType: string }>>) => (
    <div className="bg-blue-100 border-2 border-blue-400 rounded-lg p-3 min-w-[120px] text-center shadow-sm">
        <Handle type="target" position={Position.Left} className="!bg-blue-400" />
        <div className="flex items-center justify-center gap-2">
            <DatabaseIcon className="w-4 h-4 text-blue-600" />
            <span className="text-xs font-medium text-blue-800">Data Source</span>
        </div>
        <div className="text-[10px] text-blue-600 mt-1 truncate">{data.ticker || 'Any'}</div>
        <Handle type="source" position={Position.Right} className="!bg-blue-400" />
    </div>
);

const PreviewLLMNode = ({ data }: NodeProps<Node<{ model: string; prompt: string }>>) => (
    <div className="bg-purple-100 border-2 border-purple-400 rounded-lg p-3 min-w-[120px] text-center shadow-sm">
        <Handle type="target" position={Position.Left} className="!bg-purple-400" />
        <div className="flex items-center justify-center gap-2">
            <Cpu className="w-4 h-4 text-purple-600" />
            <span className="text-xs font-medium text-purple-800">LLM</span>
        </div>
        <div className="text-[10px] text-purple-600 mt-1 truncate">{data.model}</div>
        <Handle type="source" position={Position.Right} className="!bg-purple-400" />
    </div>
);

const PreviewEmailNode = ({ data }: NodeProps<Node<{ subject: string }>>) => (
    <div className="bg-green-100 border-2 border-green-400 rounded-lg p-3 min-w-[120px] text-center shadow-sm">
        <Handle type="target" position={Position.Left} className="!bg-green-400" />
        <div className="flex items-center justify-center gap-2">
            <Mail className="w-4 h-4 text-green-600" />
            <span className="text-xs font-medium text-green-800">Email</span>
        </div>
        <div className="text-[10px] text-green-600 mt-1 truncate">{data.subject}</div>
        <Handle type="source" position={Position.Right} className="!bg-green-400" />
    </div>
);

const PreviewDecisionNode = ({ data }: NodeProps<Node<{ isSmartDecision: boolean }>>) => (
    <div className="bg-yellow-100 border-2 border-yellow-400 rounded-lg p-3 min-w-[120px] text-center shadow-sm">
        <Handle type="target" position={Position.Left} className="!bg-yellow-400" />
        <div className="flex items-center justify-center gap-2">
            <Diamond className="w-4 h-4 text-yellow-600" />
            <span className="text-xs font-medium text-yellow-800">Decision</span>
        </div>
        <div className="text-[10px] text-yellow-600 mt-1">{data.isSmartDecision ? 'Smart' : 'Rule'}</div>
        <Handle type="source" position={Position.Right} className="!bg-yellow-400" />
    </div>
);

const PreviewScheduleNode = ({ data }: NodeProps<Node<{ schedule: string }>>) => (
    <div className="bg-orange-100 border-2 border-orange-400 rounded-lg p-3 min-w-[120px] text-center shadow-sm">
        <Handle type="target" position={Position.Left} className="!bg-orange-400" />
        <div className="flex items-center justify-center gap-2">
            <Clock className="w-4 h-4 text-orange-600" />
            <span className="text-xs font-medium text-orange-800">Schedule</span>
        </div>
        <div className="text-[10px] text-orange-600 mt-1 truncate">{data.schedule}</div>
        <Handle type="source" position={Position.Right} className="!bg-orange-400" />
    </div>
);

const PreviewJsonTransformNode = ({ data }: NodeProps<Node<{ jmesPath: string }>>) => (
    <div className="bg-cyan-100 border-2 border-cyan-400 rounded-lg p-3 min-w-[120px] text-center shadow-sm">
        <Handle type="target" position={Position.Left} className="!bg-cyan-400" />
        <div className="flex items-center justify-center gap-2">
            <CurlyBraces className="w-4 h-4 text-cyan-600" />
            <span className="text-xs font-medium text-cyan-800">Transform</span>
        </div>
        <Handle type="source" position={Position.Right} className="!bg-cyan-400" />
    </div>
);

const PreviewApiDestinationNode = ({ data }: NodeProps<Node<{ url: string; method: string }>>) => (
    <div className="bg-red-100 border-2 border-red-400 rounded-lg p-3 min-w-[120px] text-center shadow-sm">
        <Handle type="target" position={Position.Left} className="!bg-red-400" />
        <div className="flex items-center justify-center gap-2">
            <PlugIcon className="w-4 h-4 text-red-600" />
            <span className="text-xs font-medium text-red-800">API</span>
        </div>
        <div className="text-[10px] text-red-600 mt-1 truncate">{data.method}</div>
        <Handle type="source" position={Position.Right} className="!bg-red-400" />
    </div>
);

const previewNodeTypes = {
    'data-source': PreviewDataSourceNode,
    'llm': PreviewLLMNode,
    'email': PreviewEmailNode,
    'decision': PreviewDecisionNode,
    'schedule': PreviewScheduleNode,
    'json-transformation': PreviewJsonTransformNode,
    'api-destination': PreviewApiDestinationNode,
};

export const WorkflowPreviewComponent: React.FC<WorkflowPreviewComponentProps> = ({ workflowJson, onWorkflowCreated }) => {
    const accessToken = useAppSelector(state => state.user.credentials?.accessToken);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isCreating, setIsCreating] = useState(false);

    // Parse the workflow JSON - handle both valid workflows and validation errors
    const { workflowData, validationErrors } = useMemo(() => {
        try {
            const parsed = JSON.parse(workflowJson);
            // Check if this is a validation error response
            if (parsed.valid === false && parsed.errors) {
                return { workflowData: null, validationErrors: parsed.errors as string[] };
            }
            return { workflowData: parsed as WorkflowData, validationErrors: null };
        } catch (e) {
            console.error('Failed to parse workflow JSON:', e);
            return { workflowData: null, validationErrors: ['Failed to parse workflow data'] };
        }
    }, [workflowJson]);

    // Convert workflow data to ReactFlow nodes and edges
    const { nodes, edges } = useMemo(() => {
        if (!workflowData) {
            return { nodes: [], edges: [] };
        }

        const sortedSteps = [...workflowData.steps].sort((a, b) => a.order - b.order);
        const nodeList: Node[] = [];
        const edgeList: Edge[] = [];

        sortedSteps.forEach((step, index) => {
            const nodeId = `step-${index}`;
            const xPos = 50 + index * 160;
            const yPos = 50;

            let nodeData: Record<string, unknown> = {};

            switch (step.stepType) {
                case 'data-source':
                    nodeData = {
                        ticker: step.dataSourceStep?.ticker || '',
                        dataType: step.dataSourceStep?.subType || '',
                    };
                    break;
                case 'llm':
                    nodeData = {
                        model: step.llmStep?.model || '',
                        prompt: step.llmStep?.prompt || '',
                    };
                    break;
                case 'email':
                    nodeData = {
                        subject: step.emailStep?.subject || '',
                    };
                    break;
                case 'decision':
                    nodeData = {
                        isSmartDecision: step.decisionStep?.isSmartDecisionStep || false,
                    };
                    break;
                case 'schedule':
                    nodeData = {
                        schedule: step.scheduleStep?.schedule || '',
                    };
                    break;
                case 'json-transformation':
                    nodeData = {
                        jmesPath: step.jsonTransformationStep?.jmespath || '',
                    };
                    break;
                case 'api-destination':
                    nodeData = {
                        url: step.apiDestinationStep?.url || '',
                        method: step.apiDestinationStep?.method || '',
                    };
                    break;
            }

            nodeList.push({
                id: nodeId,
                type: step.stepType,
                position: { x: xPos, y: yPos },
                data: nodeData,
            });

            // Create edge to previous node
            if (index > 0) {
                edgeList.push({
                    id: `edge-${index - 1}-${index}`,
                    source: `step-${index - 1}`,
                    target: nodeId,
                    animated: true,
                    style: { stroke: '#f97316' },
                });
            }
        });

        return { nodes: nodeList, edges: edgeList };
    }, [workflowData]);

    // Convert to API format for creating workflow
    // Since the data already comes in API format from the backend, we just need to ensure proper typing
    const convertToApiSteps = useCallback((): Step[] => {
        if (!workflowData) return [];

        return workflowData.steps.map((step): Step => ({
            stepType: step.stepType as Step['stepType'],
            order: step.order,
            dataSourceStep: step.dataSourceStep ? {
                ticker: step.dataSourceStep.ticker,
                subType: step.dataSourceStep.subType as NonNullable<Step['dataSourceStep']>['subType'],
            } : undefined,
            llmStep: step.llmStep ? {
                model: step.llmStep.model as NonNullable<Step['llmStep']>['model'],
                prompt: step.llmStep.prompt,
                useSystemPrompt: step.llmStep.useSystemPrompt,
                structuredOutput: step.llmStep.structuredOutput,
            } : undefined,
            emailStep: step.emailStep ? {
                subject: step.emailStep.subject,
                autoTransformBody: step.emailStep.autoTransformBody,
            } : undefined,
            decisionStep: step.decisionStep ? {
                isSmartDecisionStep: step.decisionStep.isSmartDecisionStep,
                model: step.decisionStep.model,
                smartDecisionStepQuestion: step.decisionStep.smartDecisionStepQuestion,
                targetAttribute: step.decisionStep.targetAttribute,
                targetValue: step.decisionStep.targetValue,
                operator: step.decisionStep.operator as NonNullable<Step['decisionStep']>['operator'],
                attributeType: step.decisionStep.attributeType as NonNullable<Step['decisionStep']>['attributeType'],
            } : undefined,
            scheduleStep: step.scheduleStep ? {
                schedule: step.scheduleStep.schedule,
            } : undefined,
            jsonTransformationStep: step.jsonTransformationStep ? {
                jmespath: step.jsonTransformationStep.jmespath,
            } : undefined,
            apiDestinationStep: step.apiDestinationStep ? {
                url: step.apiDestinationStep.url,
                method: step.apiDestinationStep.method,
                authHeaderName: step.apiDestinationStep.authHeaderName,
                authHeaderSecretName: step.apiDestinationStep.authHeaderSecretName,
                headers: {},
                body: '',
            } : undefined,
        }));
    }, [workflowData]);

    const handleCreateWorkflow = useCallback(async () => {
        if (!workflowData || !accessToken) {
            dispatch(setNotification({
                type: 'error',
                message: 'Unable to create workflow. Please log in and try again.',
            }));
            return;
        }

        setIsCreating(true);
        try {
            const steps = convertToApiSteps();
            const workflowId = await createWorkflow(accessToken, workflowData.name, steps);
            
            dispatch(setNotification({
                type: 'success',
                message: `Workflow "${workflowData.name}" created successfully!`,
            }));

            // Trigger refresh of workflows list
            if (onWorkflowCreated) {
                onWorkflowCreated();
            }

            // Navigate to workflows page with the new workflow selected
            navigate(`/workflows?id=${workflowId}`);
        } catch (error: any) {
            console.error('Failed to create workflow:', error);
            dispatch(setNotification({
                type: 'error',
                message: error.message || 'Failed to create workflow. Please try again.',
            }));
        } finally {
            setIsCreating(false);
        }
    }, [workflowData, accessToken, convertToApiSteps, dispatch, navigate, onWorkflowCreated]);

    // Show validation errors if present
    if (validationErrors && validationErrors.length > 0) {
        return (
            <div className="my-4 border border-amber-300 rounded-lg bg-amber-50 shadow-md overflow-hidden">
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span className="text-white font-medium">Workflow Validation Errors</span>
                </div>
                <div className="p-4">
                    <p className="text-sm text-amber-800 mb-3">
                        The workflow has the following issues that need to be fixed:
                    </p>
                    <ul className="list-disc list-inside space-y-1">
                        {validationErrors.map((error, idx) => (
                            <li key={idx} className="text-sm text-amber-700">{error}</li>
                        ))}
                    </ul>
                </div>
            </div>
        );
    }

    if (!workflowData) {
        return (
            <div className="my-4 p-4 border border-red-200 rounded-lg bg-red-50">
                <div className="text-sm text-red-600">
                    Failed to parse workflow data
                </div>
            </div>
        );
    }

    return (
        <div className="my-4 border border-orange-200 rounded-lg bg-white shadow-md overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-white" />
                    <span className="text-white font-medium">{workflowData.name}</span>
                </div>
                <span className="text-orange-100 text-sm">{workflowData.steps.length} steps</span>
            </div>

            {/* Canvas Preview */}
            <div className="h-[200px] bg-gray-50">
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    nodeTypes={previewNodeTypes}
                    fitView
                    fitViewOptions={{ padding: 0.3 }}
                    proOptions={{ hideAttribution: true }}
                    nodesDraggable={false}
                    nodesConnectable={false}
                    elementsSelectable={false}
                    panOnDrag={false}
                    zoomOnScroll={false}
                    zoomOnPinch={false}
                    zoomOnDoubleClick={false}
                >
                    <Background variant={BackgroundVariant.Dots} gap={12} size={1} color="#e5e7eb" />
                </ReactFlow>
            </div>

            {/* Footer with Create Button */}
            <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                <div className="text-xs text-gray-500">
                    Preview of your workflow
                </div>
                <button
                    onClick={handleCreateWorkflow}
                    disabled={isCreating || !accessToken}
                    className={`
                        px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2
                        ${isCreating || !accessToken
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-orange-600 text-white hover:bg-orange-700 transition-colors'
                        }
                    `}
                >
                    {isCreating ? (
                        <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Creating...
                        </>
                    ) : (
                        <>
                            <Zap className="w-4 h-4" />
                            Create Workflow
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};
